import { format as formatDate, formatDistanceToNowStrict, differenceInHours } from 'date-fns';

type availableFormats =
  | 'dd/MM/yyyy'
  | 'MM/dd/yyyy'
  | 'yyyy/MM/dd'
  | 'dd/MM/yyyy, hh:mm'
  | 'MM/dd/yyyy, hh:mm'
  | 'yyyy/MM/dd, hh:mm'
  // VERA-304/VERA-924 canonical display format across the app — ordinal day,
  // 12-hour time (e.g. "Jun 5th 2026, 6:34 AM"). See working-conventions.
  | 'MMM do yyyy, h:mm a'
  /*
   * The same canonical shape WITHOUT a time, for date-only fields.
   *
   * Added because the DEFAULT below is `MM/dd/yyyy`, which is genuinely
   * ambiguous: "08/06/2026" is August 6th to a US reader and 8th June to
   * everyone else, and this app is opened by scanning a physical tag anywhere in
   * the world. A named month cannot be misread, and the ordinal day matches the
   * format already used everywhere a timestamp is shown, so a page mixing the two
   * reads as one system.
   */
  | 'MMM do yyyy';

/**
 * Parse a timestamp coming off the API, tolerating the format raw-SQL columns emit.
 *
 * Entity-mapped fields serialize as ISO, but values selected in raw SQL (scan rollups,
 * `created_at`/`updated_at` on the certificate aggregate, `tag_opened_at`) come over the
 * wire as Postgres' own rendering: `2026-07-20 05:39:29.441537` — space-separated, no
 * timezone. V8 accepts that, so it looks fine in Chrome and in Node tests, but
 * **WebKit rejects it and returns Invalid Date**. `apps/app` is reached by tapping an
 * NFC tag with a phone, so iOS Safari is the primary runtime: an unguarded
 * `format(new Date(raw))` throws `RangeError: Invalid time value` there and takes the
 * surrounding component down with it.
 *
 * Normalizes to ISO (treating the naive timestamp as UTC, which is how the API stores
 * it) and returns null rather than an Invalid Date, so callers branch instead of throw.
 */
export const parseApiDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  // `2026-07-20 05:39:29.441537` -> `2026-07-20T05:39:29.441537Z`
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(value)
    ? `${value.replace(' ', 'T')}Z`
    : value;

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatedDate = ({
  date = new Date(),
  /*
   * Defaults to the unambiguous format. Every call site that did not name one was
   * silently getting MM/dd/yyyy — including "Owned since" and "Manufacture date"
   * on the certificate page. The numeric variants stay in the union for anywhere
   * a compact date is genuinely needed, but they have to be ASKED for now.
   */
  format = 'MMM do yyyy',
}: {
  date?: string | Date;
  format?: availableFormats;
}) => {
  return formatDate(new Date(date || new Date()), format);
};

/**
 * "3 days ago" / "2 hours ago" — for statuses where RECENCY is the information and an
 * absolute date would make the reader do the subtraction themselves.
 *
 * `Strict` (rather than `formatDistanceToNow`) deliberately: the non-strict variant
 * prefixes "about"/"almost", which reads as hedging on a timestamp we recorded exactly.
 * Anything under a minute collapses to "just now", since "0 seconds ago" is noise.
 */
export const timeAgo = (date: string | Date): string => {
  const then = new Date(date);
  if (Number.isNaN(then.getTime())) return '';
  if (Date.now() - then.getTime() < 60_000) return 'just now';
  return `${formatDistanceToNowStrict(then)} ago`;
};

/**
 * Timestamp for activity rows: relative while it's recent, absolute once it isn't.
 *
 * Under 24h reads as "10 minutes ago" — for something that just happened, elapsed time
 * is what the reader actually wants, and a clock time forces them to do the subtraction.
 * Past 24h that inverts: "3 days ago" is vaguer than the date, and vaguer still as it
 * ages, so we switch to the date plus time.
 */
/** Show "N ago" up to this age, a real date beyond it. 3 days (VERA-476). */
const RELATIVE_TIME_CUTOFF_HOURS = 72;

export const formatActivityTime = (
  date?: string | Date | null,
  /**
   * Drop the time of day from the ABSOLUTE branch — "20 Aug" instead of
   * "Aug 20, 02:26". The relative branch is unaffected.
   *
   * For a LIST ROW whose text column competes with a status chip and this
   * timestamp. The Activity feed truncates its product titles to keep rows at
   * two lines, and `HH:mm` costs ~40px that the title needs more: at the point
   * a row has aged past the relative cutoff, "which day" is the question being
   * asked and the minute is noise.
   *
   * It loses nothing, and that is the actual justification rather than the
   * space: `ListRow` renders a `timeTitle`, and the Activity feed already
   * passes the full "MMM do yyyy, h:mm a" there — so the precise moment is
   * still one hover or long-press away.
   *
   * OFF by default, deliberately. The scans surfaces (`ScansList`,
   * `MapHistory`, `StatusNotification`) share this formatter, and for a SCAN
   * the time of day is part of the fact being reported — two scans on one day
   * are a different story from one. Only a caller whose row is width-starved
   * should pass it.
   */
  { dateOnly = false }: { dateOnly?: boolean } = {},
): string => {
  if (!date) return '';
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return '';

  // Relative for the first 3 days, absolute after. Rationale: within a few days
  // "2 days ago" is the thing you actually want to know (how recent is this?), while
  // past that the elapsed count stops being meaningful and a real date is easier to
  // place and compare. 24h was too short — a scan from yesterday evening flipped to a
  // bare timestamp while it was still the most recent thing that happened.
  //
  // The `>= 0` also guards clock skew / future timestamps, which would otherwise render
  // "in 3 minutes" on a scan that already happened; those fall through to the date.
  const hoursElapsed = differenceInHours(new Date(), value);
  if (hoursElapsed >= 0 && hoursElapsed < RELATIVE_TIME_CUTOFF_HOURS) {
    return `${formatDistanceToNowStrict(value)} ago`;
  }

  // "Jun 22, 01:26" rather than "06/22/2026, 01:26": an all-numeric US date is ambiguous
  // internationally and dense to scan down a column. The year only appears when it isn't
  // the current one, since repeating it on every row adds nothing.
  const isCurrentYear = value.getFullYear() === new Date().getFullYear();
  if (dateOnly) return formatDate(value, isCurrentYear ? 'd MMM' : 'd MMM yyyy');
  return formatDate(value, isCurrentYear ? 'MMM d, HH:mm' : 'MMM d yyyy, HH:mm');
};
